import { useState } from 'react';
import { api, ApiError, formatDateTime, num, scr } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { M11_ROLES, hasAnyRole } from '../../../lib/roles';
import type { TenderClarification, TenderDocument, TenderLot, EvaluationCriterion, TenderNotice } from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { ErrorNote, Field, InfoNote, Spinner, StatusBadge } from '../../../ui/components';

export function OverviewTab({ tender, onChanged }: { tender: TenderNotice; onChanged: () => void }) {
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER);

  const lotsQ = useApiQuery<{ items: TenderLot[] }>(`/api/v1/m11/tenders/${tender.id}/lots`);
  const docsQ = useApiQuery<{ items: TenderDocument[] }>(`/api/v1/m11/tenders/${tender.id}/documents`);
  const criteriaQ = useApiQuery<{ items: EvaluationCriterion[] }>(`/api/v1/m11/tenders/${tender.id}/criteria`);
  const clarQ = useApiQuery<{ items: TenderClarification[] }>(`/api/v1/m11/tenders/${tender.id}/clarifications`);

  return (
    <div className="space-y-5">
      <div className="card p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Description</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{tender.description}</p>
        {tender.cancelledReason ? (
          <div className="mt-3">
            <ErrorNote message={`Cancelled: ${tender.cancelledReason}`} />
          </div>
        ) : null}
        <SubscribeWidget category={tender.category} />
      </div>

      {isOfficer ? <LifecycleActions tender={tender} onChanged={onChanged} /> : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <LotsCard tender={tender} isOfficer={isOfficer} lots={lotsQ.data?.items ?? []} loading={lotsQ.loading} error={lotsQ.error} onAdded={lotsQ.reload} />
        <DocumentsCard tender={tender} isOfficer={isOfficer} docs={docsQ.data?.items ?? []} loading={docsQ.loading} error={docsQ.error} onAdded={docsQ.reload} />
      </div>

      <CriteriaCard tender={tender} isOfficer={isOfficer} criteria={criteriaQ.data?.items ?? []} loading={criteriaQ.loading} error={criteriaQ.error} onAdded={criteriaQ.reload} />

      <ClarificationsCard tender={tender} isOfficer={isOfficer} items={clarQ.data?.items ?? []} loading={clarQ.loading} error={clarQ.error} onChanged={clarQ.reload} />
    </div>
  );
}

function SubscribeWidget({ category }: { category: string }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subscribe = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m11/subscriptions', { category });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not subscribe');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      {error ? <ErrorNote message={error} /> : null}
      {done ? (
        <InfoNote message={`Subscribed — SeyNotify will alert you when a new "${category}" tender is published.`} />
      ) : (
        <button className="btn-secondary text-xs" onClick={subscribe} disabled={busy}>
          {busy ? 'Subscribing…' : `Get alerts for "${category}" tenders`}
        </button>
      )}
    </div>
  );
}

function LifecycleActions({ tender, onChanged }: { tender: TenderNotice; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deadline, setDeadline] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  const publish = async () => {
    if (!deadline) return;
    setBusy('publish');
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/publish`, { biddingDeadline: new Date(deadline).toISOString() });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Publish failed');
    } finally {
      setBusy(null);
    }
  };
  const close = async () => {
    setBusy('close');
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/close`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Close failed');
    } finally {
      setBusy(null);
    }
  };
  const cancel = async () => {
    setBusy('cancel');
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/cancel`, { reason: cancelReason });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Cancel failed');
    } finally {
      setBusy(null);
    }
  };

  const canCancel = !['AWARDED', 'CONTRACTED', 'CANCELLED'].includes(tender.status);

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Procurement officer actions</h2>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="flex flex-wrap items-end gap-3">
        {tender.status === 'DRAFT' ? (
          <>
            <Field label="Bidding deadline" className="!mb-0">
              <input type="datetime-local" className="field-input" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </Field>
            <button className="btn-primary" onClick={publish} disabled={busy !== null || !deadline}>
              {busy === 'publish' ? 'Publishing…' : 'Publish'}
            </button>
            {!tender.hasSealKey ? (
              <span className="text-xs text-amber-600">Assign the evaluation panel first (Evaluation tab) — it mints the sealed-bid key publishing requires.</span>
            ) : null}
          </>
        ) : null}
        {['PUBLISHED', 'CLARIFICATIONS'].includes(tender.status) ? (
          <button className="btn-secondary" onClick={close} disabled={busy !== null}>
            {busy === 'close' ? 'Closing…' : 'Close bidding'}
          </button>
        ) : null}
        {canCancel ? (
          showCancel ? (
            <div className="flex items-end gap-2">
              <Field label="Cancellation reason" className="!mb-0">
                <input className="field-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              </Field>
              <button className="btn-danger" onClick={cancel} disabled={busy !== null || cancelReason.length < 3}>
                {busy === 'cancel' ? 'Cancelling…' : 'Confirm cancel'}
              </button>
              <button className="btn-secondary" onClick={() => setShowCancel(false)}>Back</button>
            </div>
          ) : (
            <button className="btn-danger" onClick={() => setShowCancel(true)}>Cancel tender</button>
          )
        ) : null}
      </div>
    </div>
  );
}

function LotsCard({ tender, isOfficer, lots, loading, error, onAdded }: { tender: TenderNotice; isOfficer: boolean; lots: TenderLot[]; loading: boolean; error: string | null; onAdded: () => void }) {
  const [showForm, setShowForm] = useState(false);
  const [lotNo, setLotNo] = useState('1');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setFormError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/lots`, { lotNo: Number.parseInt(lotNo, 10), title });
      setShowForm(false);
      setTitle('');
      onAdded();
    } catch (e) {
      setFormError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not add lot');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Lots</h2>
        {isOfficer && tender.status === 'DRAFT' ? (
          <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>+ Add lot</button>
        ) : null}
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {showForm ? (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-200 p-3">
          {formError ? <ErrorNote message={formError} /> : null}
          <div className="grid grid-cols-3 gap-2">
            <Field label="Lot no." className="!mb-0"><input className="field-input" type="number" min="1" value={lotNo} onChange={(e) => setLotNo(e.target.value)} /></Field>
            <Field label="Title" className="col-span-2 !mb-0"><input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
          </div>
          <button className="btn-primary !py-1 text-xs" onClick={add} disabled={busy || title.length < 2}>{busy ? 'Adding…' : 'Add'}</button>
        </div>
      ) : null}
      {loading ? <Spinner /> : lots.length === 0 ? (
        <p className="text-xs text-slate-400">No lots — single-lot tender.</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {lots.map((l) => (
            <li key={l.id} className="flex justify-between rounded-lg bg-slate-50 px-3 py-2">
              <span>Lot {l.lotNo} — {l.title}</span>
              <span className="tabular-nums text-slate-500">{scr(l.estimatedValueScr)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DocumentsCard({ tender, isOfficer, docs, loading, error, onAdded }: { tender: TenderNotice; isOfficer: boolean; docs: TenderDocument[]; loading: boolean; error: string | null; onAdded: () => void }) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [docRef, setDocRef] = useState('');
  const [sha256, setSha256] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setFormError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/documents`, { title, docRef, sha256 });
      setShowForm(false);
      setTitle('');
      setDocRef('');
      setSha256('');
      onAdded();
    } catch (e) {
      setFormError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not add document');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Reference documents</h2>
        {isOfficer ? <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>+ Add document</button> : null}
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {showForm ? (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-200 p-3">
          {formError ? <ErrorNote message={formError} /> : null}
          <Field label="Title" className="!mb-0"><input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Terms of Reference" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Doc ref (object storage key)" className="!mb-0"><input className="field-input" value={docRef} onChange={(e) => setDocRef(e.target.value)} /></Field>
            <Field label="SHA-256 (64 hex chars)" className="!mb-0"><input className="field-input font-mono text-xs" value={sha256} onChange={(e) => setSha256(e.target.value)} /></Field>
          </div>
          <button className="btn-primary !py-1 text-xs" onClick={add} disabled={busy || title.length < 2 || !docRef || sha256.length !== 64}>{busy ? 'Adding…' : 'Add'}</button>
        </div>
      ) : null}
      {loading ? <Spinner /> : docs.length === 0 ? (
        <p className="text-xs text-slate-400">No reference documents attached.</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {docs.map((d) => (
            <li key={d.id} className="rounded-lg bg-slate-50 px-3 py-2">
              <div className="font-medium">{d.title}</div>
              <div className="font-mono text-[11px] text-slate-400">sha256:{d.sha256.slice(0, 16)}…</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CriteriaCard({ tender, isOfficer, criteria, loading, error, onAdded }: { tender: TenderNotice; isOfficer: boolean; criteria: EvaluationCriterion[]; loading: boolean; error: string | null; onAdded: () => void }) {
  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [maxScore, setMaxScore] = useState('100');
  const [weight, setWeight] = useState('1');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const locked = !['DRAFT', 'PUBLISHED', 'CLARIFICATIONS'].includes(tender.status);

  const add = async () => {
    setBusy(true);
    setFormError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/criteria`, { code, label, maxScore: Number.parseFloat(maxScore), weight: Number.parseFloat(weight) });
      setShowForm(false);
      setCode('');
      setLabel('');
      onAdded();
    } catch (e) {
      setFormError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not add criterion');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Evaluation criteria {locked ? <span className="ml-1 text-xs font-normal text-slate-400">(locked — bidding closed)</span> : null}</h2>
        {isOfficer && !locked ? <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>+ Add criterion</button> : null}
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {showForm ? (
        <div className="mb-3 grid grid-cols-4 gap-2 rounded-lg border border-slate-200 p-3">
          {formError ? <div className="col-span-4"><ErrorNote message={formError} /></div> : null}
          <Field label="Code" className="!mb-0"><input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="PRICE" /></Field>
          <Field label="Label" className="col-span-2 !mb-0"><input className="field-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Price competitiveness" /></Field>
          <Field label="Max score" className="!mb-0"><input className="field-input" type="number" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} /></Field>
          <Field label="Weight" className="!mb-0"><input className="field-input" type="number" value={weight} onChange={(e) => setWeight(e.target.value)} /></Field>
          <div className="col-span-4"><button className="btn-primary !py-1 text-xs" onClick={add} disabled={busy || !code || label.length < 2}>{busy ? 'Adding…' : 'Add'}</button></div>
        </div>
      ) : null}
      {loading ? <Spinner /> : criteria.length === 0 ? (
        <p className="text-xs text-slate-400">No criteria published yet{isOfficer ? ' — required before the tender can be published' : ''}.</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr><th className="table-head">Code</th><th className="table-head">Label</th><th className="table-head text-right">Max score</th><th className="table-head text-right">Weight</th></tr></thead>
          <tbody>
            {criteria.map((c) => (
              <tr key={c.id}><td className="table-cell font-mono text-xs">{c.code}</td><td className="table-cell">{c.label}</td><td className="table-cell text-right tabular-nums">{num(c.maxScore)}</td><td className="table-cell text-right tabular-nums">{num(c.weight)}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ClarificationsCard({ tender, isOfficer, items, loading, error, onChanged }: { tender: TenderNotice; isOfficer: boolean; items: TenderClarification[]; loading: boolean; error: string | null; onChanged: () => void }) {
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const canAsk = ['PUBLISHED', 'CLARIFICATIONS'].includes(tender.status);

  const ask = async () => {
    setBusy(true);
    setAskError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/clarifications`, { question });
      setQuestion('');
      onChanged();
    } catch (e) {
      setAskError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not submit the question');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Clarifications — Q&amp;A</h2>
      <p className="mb-3 text-xs text-slate-500">Public: every answer is visible to every prospective bidder, not just the asker.</p>
      {error ? <ErrorNote message={error} /> : null}
      {loading ? <Spinner /> : items.length === 0 ? (
        <p className="mb-3 text-xs text-slate-400">No questions asked yet.</p>
      ) : (
        <ul className="mb-4 space-y-3">
          {items.map((c) => (
            <li key={c.id} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">Q: {c.question}</div>
                <StatusBadge status={c.status} />
              </div>
              <div className="mt-0.5 text-[11px] text-slate-400">Asked {formatDateTime(c.createdAt)}</div>
              {c.answer ? (
                <div className="mt-2 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  A: {c.answer}
                  <div className="mt-0.5 text-[11px] text-emerald-600">Answered {formatDateTime(c.answeredAt)}</div>
                </div>
              ) : isOfficer ? (
                <AnswerForm tenderId={tender.id} clarificationId={c.id} onAnswered={onChanged} />
              ) : (
                <div className="mt-2 text-xs text-amber-600">Awaiting an officer's answer.</div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canAsk ? (
        <div className="border-t border-slate-100 pt-3">
          {askError ? <div className="mb-2"><ErrorNote message={askError} /></div> : null}
          <div className="flex gap-2">
            <input className="field-input" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask a clarification question…" />
            <button className="btn-secondary shrink-0" onClick={ask} disabled={busy || question.length < 5}>{busy ? 'Asking…' : 'Ask'}</button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Clarifications are only open while the tender is PUBLISHED or already in CLARIFICATIONS.</p>
      )}
    </div>
  );
}

function AnswerForm({ tenderId, clarificationId, onAnswered }: { tenderId: string; clarificationId: string; onAnswered: () => void }) {
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tenderId}/clarifications/${clarificationId}/answer`, { answer });
      onAnswered();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not answer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex gap-2">
      {error ? <ErrorNote message={error} /> : null}
      <input className="field-input" value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Officer's answer…" />
      <button className="btn-secondary shrink-0 !py-1 text-xs" onClick={submit} disabled={busy || !answer}>{busy ? 'Sending…' : 'Answer'}</button>
    </div>
  );
}
